import type { RequisitionTemplate } from './requisitionAssistTypes';

/**
 * Starter requisition templates (industry-style intake: general info, role scope, skills, logistics).
 * Recruiters still pick client / headcount / budget on the Details tab.
 */
export const REQUISITION_TEMPLATES: RequisitionTemplate[] = [
  {
    id: 'swe-backend',
    name: 'Software Engineer — Backend',
    category: 'Engineering',
    description: 'APIs, data layer, reliability. Typical full-time tech hire.',
    patch: {
      title: 'Software Engineer (Backend)',
      department: 'Engineering',
      employment_type: 'Full-time',
      location: 'Hybrid — specify city',
      business_justification:
        'Backfill / growth: need an engineer to own services and data integrations, improve reliability, and pair on roadmap delivery with product.',
      must_have_skills: [
        'Backend development (e.g. Node, Go, or Java)',
        'REST or gRPC APIs',
        'SQL & relational databases',
        'Git, code review, testing',
        'Production debugging',
      ],
      nice_to_have_skills: ['Docker/Kubernetes', 'Event-driven design', 'AWS/GCP basics', 'Observability (metrics/traces)'],
    },
  },
  {
    id: 'pm-senior',
    name: 'Senior Product Manager',
    category: 'Product',
    description: 'Discovery, roadmap, stakeholder management.',
    patch: {
      title: 'Senior Product Manager',
      department: 'Product',
      employment_type: 'Full-time',
      location: 'Remote-friendly',
      business_justification:
        'Drive outcome-based roadmap, align engineering and GTM, and own success metrics for a core product area.',
      must_have_skills: [
        'Product discovery & prioritisation',
        'Roadmap & stakeholder management',
        'Analytics & experimentation mindset',
        'Technical fluency with engineering',
        'Written communication & narratives',
      ],
      nice_to_have_skills: ['B2B SaaS experience', 'Pricing/packaging', 'Design partnership'],
    },
  },
  {
    id: 'csm',
    name: 'Customer Success Manager',
    category: 'Customer',
    description: 'Adoption, renewals, expansion signals.',
    patch: {
      title: 'Customer Success Manager',
      department: 'Customer Success',
      employment_type: 'Full-time',
      location: 'On-site or hybrid',
      business_justification:
        'Protect and grow ARR: onboarding, health scoring, QBRs, and coordination with support/sales on at-risk accounts.',
      must_have_skills: [
        'Account management',
        'Onboarding & training delivery',
        'Renewal / expansion motions',
        'CRM hygiene (e.g. Salesforce)',
        'Executive communication',
      ],
      nice_to_have_skills: ['SaaS metrics (NRR, churn)', 'Support escalation', 'Industry domain'],
    },
  },
  {
    id: 'data-analyst',
    name: 'Data Analyst',
    category: 'Data',
    description: 'Reporting, dashboards, ad hoc analysis.',
    patch: {
      title: 'Data Analyst',
      department: 'Data / Analytics',
      employment_type: 'Full-time',
      location: 'Hybrid',
      business_justification:
        'Provide trusted reporting for leadership and partners: KPI dashboards, deep dives, and data quality partnership with engineering.',
      must_have_skills: ['SQL', 'Spreadsheet / BI tools (e.g. Looker, Power BI)', 'Metrics definition', 'Storytelling with data'],
      nice_to_have_skills: ['dbt or ETL concepts', 'Python', 'Experiment analysis'],
    },
  },
  {
    id: 'sdr',
    name: 'Sales Development Representative',
    category: 'Sales',
    description: 'Outbound/inbound prospecting, pipeline creation.',
    patch: {
      title: 'Sales Development Representative (SDR)',
      department: 'Sales',
      employment_type: 'Full-time',
      location: 'Office or remote — specify',
      business_justification:
        'Build qualified pipeline for AEs via structured outreach, research, and disciplined follow-up in our ICP.',
      must_have_skills: ['Prospecting & sequencing', 'CRM discipline', 'Objection handling', 'Research & personalization'],
      nice_to_have_skills: ['B2B SaaS', 'Tools: LinkedIn, Apollo, etc.', 'Industry vertical experience'],
    },
  },
];
